import { Stack } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import pluralize from 'pluralize';
import * as queryString from 'query-string';
import React from 'react';

import { Checkbox } from '@sbu/components.checkbox';
import { DataGridCell, S_COLUMN_WIDTH_PX } from '@sbu/components.datagrid';
import { getSignalSeverityStatusColor } from '@sbu/utils.signals';

import { EntityListFragment } from 'core/graphql';
import { ValueBasedFilterTypes } from 'shared/components/internal/Filter';
import { addQuickFilterToQuery } from 'shared/components/internal/Filter/utils';

import { EntityContextActions } from './EntityContextActions';
import { EntityLink } from './EntityLink';

export const OBJECT_TYPE = 'Entity';

export const getSelectedRowsLabel = (
  selectedRow: EntityListFragment | undefined,
  selectedRows?: EntityListFragment[]
) => {
  const rowCount = selectedRow ? 1 : selectedRows?.length;
  return `${rowCount} ${pluralize(OBJECT_TYPE, rowCount)}`;
};

const columnHelper = createColumnHelper<EntityListFragment>();

export const getColumns = (
  onQueryChange: (query: string) => void,
  handleCheckboxClickAll: (
    rows: EntityListFragment[],
    isSelected: boolean
  ) => void,
  handleCheckboxClick: (row: EntityListFragment, isSelected: boolean) => void,
  insightConfigThreshold?: number | null
) => [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        CheckboxProps={{
          indeterminate: table.getIsSomeRowsSelected(),
          sx: { padding: 0 },
        }}
        data-sumo-test='entities-data-grid-select-all'
        hideLabel
        label='Select all Entities'
        onChange={(_, checked) => {
          table.toggleAllRowsSelected(checked);
          handleCheckboxClickAll(
            table.getRowModel().rows.map((row) => row.original),
            checked
          );
        }}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        CheckboxProps={{
          sx: { padding: 0 },
        }}
        data-sumo-test='entities-data-grid-select-checkbox'
        hideLabel
        label={`Select ${row.original.id} ${row.original.value}`}
        onChange={(_, checked) => {
          row.toggleSelected();
          handleCheckboxClick(row.original, checked);
        }}
        onClick={(e) => e.stopPropagation()} // Prevent row click when clicking checkbox
      />
    ),
    size: 50,
    enableResizing: false,
    meta: {
      disableReordering: true, // disable column reordering for the checkbox column
    },
  }),
  // The accessor id takes precendence over display column id so if you have different ids,
  // column reordering and sorting will not work correctly. Use row accessor instead.
  columnHelper.accessor((row) => row.value, {
    header: () => <DataGridCell>Entity</DataGridCell>,
    cell: (info) => (
      <DataGridCell
        title={info.row.original.value}
        renderActions={() => (
          <EntityContextActions
            entity={info.row.original}
            onQueryChange={onQueryChange}
          />
        )}
        variant='body1'
      >
        <EntityLink entity={info.row.original} />
      </DataGridCell>
    ),
    id: 'name', // needed to map the correct id for sorting
    enableSorting: true,
    size: S_COLUMN_WIDTH_PX,
  }),
  // The accessor id takes precendence over display column id so if you have different ids,
  // column reordering and sorting will not work correctly. Use row accessor instead.
  columnHelper.accessor((row) => row.entityTypeName, {
    header: () => <DataGridCell>Entity Type</DataGridCell>,
    cell: (info) => (
      <DataGridCell variant='body1'>
        {info.row.original.entityTypeName}
      </DataGridCell>
    ),
    id: 'type', // needed to map the correct id for sorting
    enableSorting: true,
    size: S_COLUMN_WIDTH_PX,
  }),
  columnHelper.accessor('activityScore', {
    header: () => <DataGridCell>Activity Score</DataGridCell>,
    cell: (info) => (
      <DataGridCell textAlign='right' variant='body1'>
        {info.getValue()}
      </DataGridCell>
    ),
    enableSorting: true,
    size: S_COLUMN_WIDTH_PX,
  }),
  columnHelper.accessor('recentSignalSeverity', {
    header: () => <DataGridCell>Signal Severity Total</DataGridCell>,
    cell: (info) => (
      <DataGridCell
        color={getSignalSeverityStatusColor(
          info.getValue(),
          insightConfigThreshold
        )}
        textAlign='right'
        variant='body1'
      >
        {info.getValue()}
      </DataGridCell>
    ),
    enableSorting: true,
    size: S_COLUMN_WIDTH_PX,
  }),
  columnHelper.accessor('suppressListsObj', {
    header: () => <DataGridCell>Suppressed Lists</DataGridCell>,
    cell: (info) => (
      <DataGridCell variant='body1'>
        <Stack gap={0.5}>
          {info.getValue().length
            ? info
                .getValue()
                .map((suppressedListObj) => suppressedListObj.name)
                .join(', ')
            : '-'}
        </Stack>
      </DataGridCell>
    ),
    size: S_COLUMN_WIDTH_PX,
  }),
  columnHelper.accessor('criticality', {
    header: () => <DataGridCell>Criticality</DataGridCell>,
    cell: (info) => (
      <DataGridCell title={info.getValue() ?? 'Default'} variant='body1'>
        {info.getValue() ?? 'Default'}
      </DataGridCell>
    ),
    size: S_COLUMN_WIDTH_PX,
  }),
];

export const onQuickFilter = (
  filter: ValueBasedFilterTypes,
  onQueryChange: (query: string) => void
) => {
  const { q } = queryString.parse(location.search);
  const newQuery = addQuickFilterToQuery(filter, q as string, '');
  onQueryChange(newQuery);
};
